import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hot } from 'react-hot-loader/root';
import moment from 'moment';

import { Card, Button } from 'antd';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import withErrorHandler from '../../hoc/withErrorHandler';
import axios from '../../shared/axios/axios';
import * as actions from './store/actions';

import CalendarMain from './components/calendar-main';
import CalendarSidebar from './components/calendar-sidebar/calendar-sidebar';
import CalendarTimeZoneInfo from './components/calendar-time-zone-info/calendar-time-zone-info';
import EditCategoryModal from './components/modals/edit-category-modal';

import { toVBColor } from './utils/color-utils';

import { getCategoriesWithSetting, getCategoryWithSetting, getFilters } from './helpers/calendar-data-helper';

import './calendar-scheduler.less';

let root;

const CalendarSchedulerComponent = (props) => {
  root = props.rootContainer;

  const dispatch = useDispatch();

  const [timeZoneInfo, setTimeZoneInfo] = useState(null);

  const [sidebarSelectedDate, setSidebarSelectedDate] = useState(moment());

  const groups = getFilters();
  const [chosenGroups, setChosenGroups] = useState([]);

  const [extendedCategories, setExtendedCategories] = useState(null);
  const [chosenCategories, setChosenCategories] = useState(null);

  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [editCategoryModalTitle, setEditCategoryModalTitle] = useState(null);
  const [categoryToEdit, setCategoryToEdit] = useState(null);

  const [settings, setSettings] = useState({
    sidebarVisible: true,
    showAllEvents: false,
  });

  const { calendarCategoriesLoading, calendarCategorySettingsLoading } = useSelector((state) => state.calendarScheduler);

  useEffect(() => {
    const selectedGroups = [];

    groups.map((g) => {
      if (g.checked) {
        selectedGroups.push(g.id);
      }
    });

    setChosenGroups(selectedGroups);

    loadCalendarFilterData();
  }, []);

  const loadCalendarFilterData = (skipCheckedCategories) => {
    dispatch(actions.getCalendarCategories()).then((categories) => {
      dispatch(actions.getCalendarCategorySettings()).then((settings) => {
        setTimeZoneInfo(settings.timeZoneInfo);

        let categoriesWithSettings = getCategoriesWithSetting(categories, settings.categorySettings);
        setExtendedCategories(categoriesWithSettings);

        if (!chosenCategories && !skipCheckedCategories) {
          const selectedCategories = chosenCategories || [];

          categoriesWithSettings.forEach((c) => {
            if (c.checked) {
              selectedCategories.push(c.id);
            }
          });

          setChosenCategories(selectedCategories);
        }
      });
    });
  };

  const sidebarDateChangeCallback = (momentDateObj) => {
    setSidebarSelectedDate(momentDateObj);
  };

  const sidebarCategoriesCallback = (selectedCategories) => {
    if (selectedCategories) {
      setChosenCategories(selectedCategories);
    }
  };

  const sidebarFiltersCallback = (selectedFilters) => {
    if (selectedFilters) {
      setChosenGroups(selectedFilters);
    }
  };

  const handleSaveCategory = (category) => {
    const newCalendarCategory = {
      id: category.id,
      categoryName: category.categoryName,
      color: toVBColor(category.color),
    };

    return new Promise((resolve, reject) => {
      dispatch(actions.saveCategory(newCalendarCategory)).then((savedId) => {
        const newCalendarcategorySetting = {
          id: category.settingId,
          displayByDefault: category.displayByDefault,
          sendEmails: category.sendEmails,
          calendarCategoryId: savedId,
        };

        dispatch(actions.saveCategorySetting(newCalendarcategorySetting)).then((savedSettingId) => {
          setIsEditCategoryModalOpen(false);

          const categoryWithSetting = getCategoryWithSetting(
            { ...newCalendarCategory, ...{ id: savedId, color: category.color } },
            { ...newCalendarcategorySetting, ...{ id: savedSettingId } },
          );
          loadCalendarFilterData(true);

          if (categoryWithSetting?.checked) {
            const selectedCategories = chosenCategories || [];
            selectedCategories.push(categoryWithSetting.id);
            setChosenCategories(selectedCategories);
          }
          resolve();
        });
      });
    });
  };

  const handleCancelSaveCategory = () => {
    setIsEditCategoryModalOpen(false);
  };

  const editCategoryHandler = (category) => {
    setEditCategoryModalTitle('Edit Category');
    setIsEditCategoryModalOpen(true);
    setCategoryToEdit(category);
  };

  const deleteCategoryHandler = (categoryId) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.deleteCategory(categoryId)).then(() => {
        loadCalendarFilterData();
        resolve();
      });
    });
  };

  const addCategoryHandler = () => {
    setEditCategoryModalTitle('Add Category');
    setIsEditCategoryModalOpen(true);
    setCategoryToEdit(null);
  };

  const toggleSidebar = () => {
    setSettings({ ...settings, ...{ sidebarVisible: !settings.sidebarVisible } });
  };

  const toggleShowAllEventItems = (domEvent) => {
    domEvent.preventDefault();
    domEvent.stopPropagation();

    setSettings({ ...settings, ...{ showAllEvents: !settings.showAllEvents } });
  };

  return (
    <div className="calendar-scheduler-wrapper calendar-scheduler-background">
      <div className={settings.sidebarVisible ? '' : 'hidden'}>
        <CalendarSidebar
          rootContainer={root}
          loading={calendarCategoriesLoading || calendarCategorySettingsLoading}
          groupFilters={groups}
          extendedCategories={extendedCategories}
          onEditCategory={editCategoryHandler}
          onDeleteCategory={deleteCategoryHandler}
          onAddCategory={addCategoryHandler}
          selectedCategories={chosenCategories}
          selectedGroups={chosenGroups}
          selectedDate={sidebarSelectedDate}
          dateChangeCallback={(momentDateObj) => sidebarDateChangeCallback(momentDateObj)}
          categoriesChangeCallback={(categories) => sidebarCategoriesCallback(categories)}
          filtersChangeCallback={(filters) => sidebarFiltersCallback(filters)}
        />
      </div>

      <div
        onClick={($event) => toggleSidebar()}
        title={(settings.sidebarVisible ? 'Collapse' : 'Expand') + ' the left pane '}
        className="ant-divider ant-divider-vertical collapsible-calendar-scheduler-divider"
      >
        <Button icon={settings.sidebarVisible ? <LeftOutlined /> : <RightOutlined />} size="small" />
      </div>

      <Card size="small" style={{ width: '100%' }} title={<CalendarTimeZoneInfo timeZoneInfo={timeZoneInfo} />}>
        <CalendarMain
          rootContainer={root}
          calendarCategories={extendedCategories}
          sidebarSelectedCategories={chosenCategories}
          sidebarSelectedGroups={chosenGroups}
          sidebarSelectedDate={sidebarSelectedDate}
          onSelectedDateChange={(momentDateObj) => sidebarDateChangeCallback(momentDateObj)}
          settings={settings}
          onToggleEvents={toggleShowAllEventItems}
          onAddCategory={addCategoryHandler}
        />
      </Card>

      <EditCategoryModal
        rootContainer={root}
        isOpen={isEditCategoryModalOpen}
        title={editCategoryModalTitle}
        categoryToEdit={categoryToEdit}
        categoriesLoading={calendarCategoriesLoading || calendarCategorySettingsLoading}
        handleModalOk={handleSaveCategory}
        handleModalCancel={handleCancelSaveCategory}
      />
    </div>
  );
};

export default hot(withErrorHandler(CalendarSchedulerComponent, axios, root));
